//* Import react hooks
import { useState } from "react";

//* Import react-query hooks
import { useMutation } from "@tanstack/react-query";

//! Import API functions
import { demandsTagAPI } from "../demandTags/demandTagAPI"; 

//? Import types
import { DemandTag, CreateDemandTagInput } from "../demandTags/types/types"; 

/**
 * Custom hook to manage demand tags.
 *
 * @param {string} token - The authentication token.
 * @returns {object} - An object containing the demand tags, error state, functions to create and delete demand tags, and loading states.
 *
 * @property {DemandTag[]} demandstags - The list of demand tags.
 * @property {string | null} error - The error message, if any.
 * @property {function} createDemand - Function to create a new demand tag.
 * @property {function} deleteDemand - Function to delete an existing demand tag.
 * @property {boolean} isCreating - Boolean indicating if a demand tag is being created.
 * @property {boolean} isDeleting - Boolean indicating if a demand tag is being deleted.
 */
export const useDemandsTags = (token: string) => {

  const [demandstags, setDemandstags] = useState<DemandTag[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  // Mutation para criar DemandTag
  const createDemandMutation = useMutation({
    mutationFn: (demandData: CreateDemandTagInput) =>
      demandsTagAPI.create(demandData, token),
    onMutate: () => {
      setIsCreating(true);
      setError(null);
    },
    onSuccess: (newDemandTag) => {
      setDemandstags((prev) => [...prev, newDemandTag]);
    },
    onError: (err: any) => {
      setError(err.message || "Falha ao criar DemandTag");
    },
    onSettled: () => {
      setIsCreating(false);
    },
  });

  // Mutation para deletar DemandTag
  const deleteDemandMutation = useMutation({
    mutationFn: (id: string) => demandsTagAPI.delete(id, token),
    onMutate: () => {
      setIsDeleting(true);
      setError(null);
    },
    onSuccess: (_, id) => {
      setDemandstags((prev) => prev.filter((tag) => tag.id !== id));
    },
    onError: (err: any) => {
      setError(err.message || "Falha ao deletar DemandTag");
    },
    onSettled: () => {
      setIsDeleting(false);
    },
  });

  // Função para criar um DemandTag
  const createDemand = (demandData: CreateDemandTagInput) => {
    createDemandMutation.mutate(demandData);
  };

  // Função para deletar um DemandTag
  const deleteDemand = (id: string) => {
    deleteDemandMutation.mutate(id);
  };

  return {
    demandstags,
    error,
    createDemand,
    deleteDemand,
    isCreating,
    isDeleting,
  };
};
